import React from "react";
import ReactDOM from "react-dom";
import { Activity } from "../../types";

export interface ActivityContextMenuProps {
  visible: boolean;
  x: number;
  y: number;
  activity: Activity | null;
  nextActivity: Activity | null;
  currentActivityId?: string;
  onClose: () => void;
  onViewDetails: (activity: Activity) => void;
  onToggleExclude: (activity: Activity) => void;
  onMergeWithNext: (activity: Activity, nextActivity: Activity) => void;
  onDelete: (activityId: string, activityTitle?: string) => void;
  onSaveAsRoute?: (activity: Activity) => void;
}

export const ActivityContextMenu: React.FC<ActivityContextMenuProps> = ({
  visible,
  x,
  y,
  activity,
  nextActivity,
  currentActivityId,
  onClose,
  onViewDetails,
  onToggleExclude,
  onMergeWithNext,
  onDelete,
  onSaveAsRoute,
}) => {
  if (!visible || !activity) return null;

  const isNextActivityActive = nextActivity?.id === currentActivityId;

  return ReactDOM.createPortal(
    <>
      {/* Transparent overlay to capture clicks */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          background: "transparent",
        }}
        onClick={onClose}
      />
      <div
        className="activity-context-menu"
        style={{
          position: "fixed",
          left: `${x}px`,
          top: `${y}px`,
          zIndex: 100000,
        }}
      >
        <div className="activity-context-menu-header">
          {activity.title || "Untitled Activity"}
        </div>

        <button
          className="activity-context-menu-item"
          onClick={() => {
            onViewDetails(activity);
            onClose();
          }}
        >
          📊 View details
        </button>

        {onSaveAsRoute && (
          <button
            className="activity-context-menu-item"
            onClick={() => {
              onSaveAsRoute(activity);
              onClose();
            }}
          >
            🚴 Start route from activity
          </button>
        )}

        <button
          className="activity-context-menu-item"
          onClick={() => {
            onToggleExclude(activity);
            onClose();
          }}
        >
          {activity.excluded ? "✅ Include in stats" : "❌ Exclude from stats"}
        </button>

        {/* Only show merge option if there's a next activity */}
        {nextActivity && (
          <button
            className="activity-context-menu-item"
            style={{
              color: isNextActivityActive
                ? "var(--warning)"
                : "var(--text-primary)",
            }}
            onClick={() => {
              onMergeWithNext(activity, nextActivity);
              onClose();
            }}
          >
            {isNextActivityActive
              ? `⚠️ Merge into active recording`
              : `🔀 Merge with next activity`}
          </button>
        )}

        <button
          className="activity-context-menu-item activity-context-menu-item--danger"
          onClick={() => {
            onDelete(activity.id, activity.title);
            onClose();
          }}
        >
          🗑️ Delete
        </button>
      </div>
    </>,
    document.body,
  );
};
